import { isAbortError } from '#/lib/isAbortError';
import { format } from 'date-fns';
import { action, makeAutoObservable, runInAction } from 'mobx';
import { SuperAgentRequest } from 'superagent';
import JucyAPI from '../lib/JucyAPI.js';
import FleetCategoryAvailability from '../lib/api/model/FleetCategoryAvailability';
import type { CatalogItem } from '../lib/api/model/RentalCatalog';
import { setTime } from '../lib/dates';
import { resolveError } from '../lib/resolveError';
import { FleetType } from '../services';
import fleetTypesStore from './FleetTypesStore';
import productStore from './ProductStore';
import rentalTripSearchStore from './RentalTripSearchStore';
import appStateStore from '#/store/AppStateStore';
import sitesStore from '#/store/SitesStore';

class MotorhomeAvailabilityStore {
    fleetCategories: FleetCategoryAvailability[] = [];
    state: 'loading' | 'pending' | 'error' | 'done' = 'pending';
    message = '';
    error?: Error;
    lastRequest?: SuperAgentRequest;

    constructor() {
        makeAutoObservable(this);
    }

    getAvailability() {
        this.state = 'loading';
        this.message = '';
        if (this.lastRequest) {
            this.lastRequest.abort();
        }
        const request = {
            typeId: 'motorhome',
            pickUpLocation: rentalTripSearchStore.pickUpLocation?.SiteCode,
            dropOffLocation: rentalTripSearchStore.dropOffLocation?.SiteCode,
            pickUpDateTime: format(setTime(rentalTripSearchStore.pickUpDate as Date, rentalTripSearchStore.pickUpTime as Date), 'yyyy-MM-dd HH:mm'),
            dropOffDateTime: format(setTime(rentalTripSearchStore.dropOffDate as Date, rentalTripSearchStore.dropOffTime as Date), 'yyyy-MM-dd HH:mm'),
            driverAge: rentalTripSearchStore.driverAge,
            campaignCode: rentalTripSearchStore.promoCode,
        };

        this.lastRequest = JucyAPI.getAvailability(request);

        return this.lastRequest
            .then((res) => res.body)
            .then(
                action((response) => {
                    if (response.ResponseType !== JucyAPI.responseTypes.success) {
                        this.message = response.Message;
                        this.state = 'error';
                    } else {
                        this.fleetCategories =
                            response.Data[1] &&
                            response.Data[1].Categories.map((apiFleetCategory: Partial<FleetCategoryAvailability>) =>
                                FleetCategoryAvailability.fromApi({
                                    apiFleetCategory,
                                    fleetType: fleetTypesStore.fleetTypes.find((f) => f.slug === 'motorhome') as FleetType,
                                    catalogData: productStore.getProductByCode(apiFleetCategory.CategoryCode, rentalTripSearchStore.pickUpLocation?.CountryCode) as CatalogItem,
                                })
                            );
                        this.state = 'done';
                    }
                    return response;
                })
            )
            .catch((e) =>
                resolveError(e).then(({ error, message }) => {
                    if (isAbortError(error)) {
                        return;
                    }
                    runInAction(() => {
                        this.state = 'error';
                        this.error = error;
                        this.message = message;
                    });
                    return error;
                })
            );
    }

    get shouldShowMotorhomeAvailability() {
        const minDriverAge = fleetTypesStore.getFleetTypeBySlug('motorhome')?.youngestDriver;
        const isDirect = appStateStore.mode === 'direct';
        const isCampervan = rentalTripSearchStore?.fleetType?.slug === 'campervan';
        const isValidAge = !minDriverAge || Boolean(rentalTripSearchStore.driverAge && Number(rentalTripSearchStore.driverAge) >= minDriverAge);
        const validSites = sitesStore.sites.filter((s) => s.SiteSettings?.find((ss) => ss.FleetTypeName.toLowerCase().includes('motorhome'))).map((s) => s.SiteCode);
        const isValidLocations =
            rentalTripSearchStore.pickUpLocation && rentalTripSearchStore.dropOffLocation
                ? validSites.includes(rentalTripSearchStore.pickUpLocation.SiteCode) && validSites.includes(rentalTripSearchStore.dropOffLocation.SiteCode)
                : false;

        return isDirect && isCampervan && isValidAge && isValidLocations;
    }
}

const motorhomeAvailabilityStore = new MotorhomeAvailabilityStore();
export default motorhomeAvailabilityStore;
